import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  ArrowRight, ArrowDown, Check, ShieldCheck, Trophy, Flame, TrendingUp,
  ShoppingBag, Users, Clock, Award, BookOpen, Quote, Calendar, Target,
  Zap, Heart, Utensils, Sparkles, Phone, Mail, Wheat, ChefHat, Send,
  Medal, Star, GraduationCap, CalendarDays, MapPin, Briefcase, Building2,
} from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const D10 = {
  phoneDisplay: "+385 95 501 1065",
  phoneE164: "385955011065",
  email: "d10spizzafantasista@gmail.com",
};

const COLORS = {
  primary: "#0087E6", navy: "#002B5C", light: "#F0F9FF",
  white: "#FFFFFF", gold: "#FFD700", accent: "#00D4AA",
};

const COURSES = {
  playmaker: {
    id: "playmaker", title: "The Playmaker", subtitle: "Pizza Enthusiast",
    level: "Starter", price: "€95", duration: "1 Day", sessions: "Single Session",
    target: "Beginners and home bakers", tagline: "Your first touch on the dough.",
    description: "A relaxed, hands-on intro where you learn the basics, laugh a bit, and leave with real skills. We keep it simple, practical, and fun. You make pizza, you eat pizza, you go home feeling dangerous.",
    highlights: ["Perfect for complete beginners", "You make and eat your own pizzas", "All equipment included", "Small group (max 8 people)"],
    modules: [
      { title: "Dough Basics", desc: "Hydration, timing, and what actually matters" },
      { title: "Stretching", desc: "Hands only, no rolling pin drama" },
      { title: "Home Oven Tricks", desc: "How to get great results at home" },
      { title: "Toppings That Work", desc: "Simple combos that always land" },
      { title: "Timing + Routine", desc: "A repeatable flow you can use weekly" },
    ],
    kit: ["D10 Apron", "Dough Box", "Specialty Flour (2kg)", "Starter Yeast Kit"],
    icon: Flame, color: COLORS.accent, image: "/img/academy/d10pizza-academy-4.jpg",
       dates: [], // Removed dates for Manager

  },
  captain: {
    id: "captain", title: "The Captain", subtitle: "Pizzaiolo Track",
    level: "Professional", price: "€750", duration: "6 Days", sessions: "Intensive Bootcamp",
    target: "Aspiring pros and serious learners", tagline: "Lead the station.",
    description: "For people who want to get really good, fast. You learn the craft, the rhythm of service, and how to stay calm when things get busy. You leave confident, consistent, and ready for a real kitchen.",
    highlights: ["Certificate of completion", "Service flow and station discipline", "Real kitchen pace (no cosplay)", "Internship options (limited)"],
    modules: [
      { title: "Dough Consistency", desc: "Repeatable results across different days" },
      { title: "Fermentation Control", desc: "Temperature, time, and smart adjustments" },
      { title: "Oven + Timing", desc: "Speed, positioning, and clean execution" },
      { title: "Style Range", desc: "Modern classics, plus what guests actually love" },
      { title: "Service Under Pressure", desc: "Prep, pace, communication, quality checks" },
      { title: "Ingredient Handling", desc: "Cheese, sauce, seasonality, and balance" },
    ],
    kit: ["Certificate", "Chef Uniform", "Internship Option (subject to availability)", "Post-course support"],
    icon: Award, color: COLORS.primary, image: "/img/academy/d10pizza-academy-9.jpg",
    dates: [
            { label: "Aug 31, 2026", value: "2026-08-31" },

    ],
  },
  manager: {
    id: "manager", title: "The Manager", subtitle: "Business Mastery",
    level: "Executive", price: "€3,750", duration: "13 Days", sessions: "Mentorship Program",
    target: "Owners, entrepreneurs, investors", tagline: "Build the system, not just the pizza.",
    description: "This is for people who want to open a place or level one up. We talk reality: pricing, costs, staffing, suppliers, routines, and brand. You leave with a clear plan and the tools to run it without guessing.",
    highlights: ["Real numbers and real templates", "Supplier introductions", "Operations playbook", "1-on-1 mentorship included"],
    modules: [
      { title: "Positioning", desc: "What you are and what you are not" },
      { title: "Menu + Pricing", desc: "Food cost and simple pricing logic" },
      { title: "Inventory + Waste", desc: "Control the chaos before it controls you" },
      { title: "Compliance", desc: "Safety standards and documentation overview" },
      { title: "Staff + Shifts", desc: "Hiring, training, rhythm, retention" },
      { title: "Money Flow", desc: "Cash flow, tools, and basic reporting" },
      { title: "Marketing", desc: "Content, PR, and how to stay top of mind" },
    ],
    kit: ["Operations Blueprint", "Templates Pack", "Supplier Network Access", "12-Month Mentorship"],
    icon: Trophy, color: COLORS.gold, image: "/img/academy/d10pizza-academy-11.jpg",
    dates: [], // Removed dates for Manager
  },
};

const FLEXIBLE_DATE_VALUE = "flexible";
const FLEXIBLE_DATE_LABEL = "I'm flexible — help me find a date";

const INSTRUCTORS = [
  { name: "Nikša Čulić", role: "Nothing against Split. Everything for great pizza.", origin: "Croatia", img: "/img/team/niksa.png" },
  { name: "Mario Markušić", role: "20+ years, hundreds of pizzas per day — this is pizza life", origin: "Croatia", img: "/img/team/mario.png" },
  { name: "Adrian Fetahu", role: "Passion and patience. A Buddhist approach to a sometimes cruel pizza world.", origin: "Croatia", img: "/img/team/adrian.png" },
  { name: "Jakov Tošić", role: "He’s not the chef at Piccaso Split by chance — every pizza he makes is a work of art.", origin: "Croatia", img: "/img/team/jakov.png" },
];

const SPECIAL_GUESTS = [
  {
    name: "Jędrzej Lewandowski", headline: "Special guest for consultation",
    role: "AVPN Pizzaiolo Verace • Zielona Górka • World championship award winner",
    origin: "Poland", img: "/img/team/jedrzej.png",
    details: "Spiritus movens of Zielona Górka — the pizzeria that brought Pabianice the AVPN award for best pizzeria in the world in 2024, with a long-standing presence on the 50 Top Pizza Europe/World list and many other awards. Also responsible for the first Polish pizzaiola in Croatia.",
  },
  {
    name: "Cristiano de Rinaldi", headline: "AVPN master trainer & consultant",
    role: "Casa de Rinaldi (Naples) • 3rd generation • Winner of the 2022 True Neapolitan Pizza Olympics",
    origin: "Italy", img: "/img/team/cristiano.png",
    details: "Cristiano de Rinaldi is a prominent Italian chef, master pizza maker, and consultant. He represents the third generation of his family's restaurant tradition at Casa de Rinaldi in Naples. In 2022, he triumphed at the Olympics of True Neapolitan Pizza.",
  },
   {
    name: "Greg Yurkovich",
    headline: "Special guest for consultation",
    role: "Pop’s Pizza Ljubljana • AVPN Certified Pizzaiolo • 50 Top Pizza Europe",
    origin: "USA / Slovenia",
    img: "/img/team/greg.png",
    details: "American-born pizzaiolo who moved from finance into building one of Europe’s top-ranked pizzerias in Ljubljana. Founder of the Pop’s brand (Pizza, Burger, Aperitivo), known for contemporary Neapolitan pizza, sourdough fermentation, and high-volume consistency. Ranked multiple times in 50 Top Pizza Europe and AVPN certified, with a strong focus on authentic ingredients and in-house product development.",
  },
];

const STATS = [
  { value: "Small groups", label: "Hands-on learning", icon: Users },
  { value: "Practical", label: "No fluff", icon: Target },
  { value: "98%", label: "Happy students", icon: Heart },
];

const GALLERY = [
  { url: "/img/academy/d10pizza-academy-8.jpg", alt: "Pizza making" },
  { url: "/img/academy/d10pizza-academy-2.jpg", alt: "Fresh pizza" },
  { url: "/img/academy/d10pizza-academy-12.jpg", alt: "Dough" },
  { url: "/img/academy/d10pizza-academy-6.jpg", alt: "Kitchen vibe" },
  { url: "/img/academy/d10pizza-academy-13.jpg", alt: "Ingredients" },
  { url: "/img/academy/d10pizza-academy-1.jpg", alt: "Class in session" },
];

const EVENTS = [
  { title: "Make Your Own Dinner", price: "€50", note: "per person", desc: "Easy evening session. You stretch, top, bake, eat. Wine included.", icon: Utensils, capacity: "2 to 12 people" },
  { title: "Kids Pizza Party", price: "€40", note: "per child", desc: "Birthday chaos, the good kind. Kids make the mess, we handle the cleanup.", icon: Sparkles, capacity: "6 to 15 kids" },
  { title: "Corporate Team Building", price: "Custom", note: "on request", desc: "Team spirit around pizza. Competitive or chill format.", icon: Users, capacity: "10 to 50 people" },
  { title: "Private Masterclass", price: "€200", note: "per session", desc: "One-on-one focused training. Your pace, your goals.", icon: Target, capacity: "1 to 2 people" },
];

const FAQ = [
  { q: "Do I need any prior experience?", a: "Nope. The Playmaker is built for beginners. The Captain helps if you have kitchen experience, but it is not required. The Manager is business-first." },
  { q: "What should I bring?", a: "Just yourself and comfy clothes you do not mind getting flour on. We provide equipment, ingredients, and aprons. Closed-toe shoes please." },
  { q: "Can I get a refund if I cannot attend?", a: "Full refund up to 14 days before. 50% up to 7 days. Inside 7 days, we can reschedule you to a future date." },
  { q: "Do you offer payment plans?", a: "Yes. Captain and Manager can be split into 3 monthly payments. Send us a message and we will sort it." },
  { q: "Is food included?", a: "Always. You eat what you make. Multi-day courses include lunch. Dietary needs are fine with advance notice." },
  { q: "Is this more serious or more fun?", a: "Both. We keep the vibe relaxed, but we do not waste your time. You will learn properly and have a good time doing it." },
];

const PARTNER_JOBS = [
{
  id: "frankos-zagreb-pizzaiolo",
  pizzeria: "Franko's Pizza & Bar",
  location: "Zagreb, Croatia",
  role: "Junior Pizzaiolo",
  type: "Full Time",
  desc: "Franko's Pizza & Bar in Zagreb is looking for a pizzaiolo to join the team in one of Croatia’s most recognized pizza destinations. A great fit for someone who wants to work in a fast-paced environment focused on quality, consistency, and serious pizza culture.",
  logoUrl: "/img/academy/partners/frankos.jpg",
  logoText: "FR",
},
{
  id: "picasso-split-pizzaiolo",
  pizzeria: "Picasso",
  location: "Split, Croatia",
  role: "Junior Pizzaiolo",
  type: "Full Time",
  desc: "Picasso in Split is looking for a pizzaiolo to join the kitchen team. This is a strong opportunity for someone who enjoys working in a busy coastal setting and wants to be part of a pizzeria focused on delivering solid pizza every service.",
  logoUrl: "/img/academy/partners/picasso.jpg",
  logoText: "PI",
},
{
  id: "pizzeria14-zagreb-pizzaiolo",
  pizzeria: "Pizzeria 14",
  location: "Zagreb, Croatia",
  role: "Junior Pizzaiolo",
  type: "Full Time",
  desc: "Pizzeria 14 in Zagreb is looking for a pizzaiolo to join its team. The role suits someone who is comfortable working with dough, handling service rhythm, and contributing to a kitchen that values reliability, pace, and well-executed pizza.",
  logoUrl: "/img/academy/partners/pizzeria14.jpg",
  logoText: "14",
},
{
  id: "porta-salina-pag-pizzaiolo",
  pizzeria: "Porta Salina",
  location: "Pag, Croatia",
  role: "Junior Pizzaiolo",
  type: "Full Time",
  desc: "Porta Salina in Pag is looking for a pizzaiolo to join the team on the island. A strong option for someone who wants to work in a seasonal hospitality environment, be part of a well-known local spot, and help deliver quality pizza in a unique coastal setting.",
  logoUrl: "/img/academy/partners/portasalina.jpg",
  logoText: "PS",
},
];

/* ──────────────────────────────────────────────────────────────
   UTILS
────────────────────────────────────────────────────────────── */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ duration: 0.25, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const SectionHeader = ({ eyebrow, title, subtitle, light = false, center = true }) => (
  <div className={`${center ? "text-center" : ""} mb-12 md:mb-16`}>
    {eyebrow && (
      <Reveal>
        <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-4 ${light ? "bg-white/10 text-white/90" : "bg-[#0087E6]/10 text-[#0087E6]"}`}>
          {eyebrow}
        </span>
      </Reveal>
    )}
    <Reveal delay={0.05}>
      <h2 className={`font-anton text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9] ${light ? "text-white" : "text-[#002B5C]"}`}>
        {title}
      </h2>
    </Reveal>
    {subtitle && (
      <Reveal delay={0.1}>
        <p className={`mt-4 md:mt-6 font-serif italic text-lg md:text-xl max-w-2xl ${center ? "mx-auto" : ""} ${light ? "text-white/70" : "text-gray-500"}`}>
          {subtitle}
        </p>
      </Reveal>
    )}
  </div>
);

const Badge = ({ children, variant = "primary" }) => {
  const styles = { primary: "bg-[#0087E6]/10 text-[#0087E6]", light: "bg-white/10 text-white", dark: "bg-[#002B5C] text-white", gold: "bg-[#FFD700]/20 text-[#FFD700]" };
  return <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${styles[variant]}`}>{children}</span>;
};

const UpcomingDates = ({ dates, variant = "light" }) => {
  const styles = {
    light: { wrapper: "bg-[#F0F9FF] border border-[#0087E6]/15", label: "text-[#002B5C]/70", icon: "text-[#0087E6]", pill: "bg-white border border-[#0087E6]/20 text-[#002B5C]", dot: "bg-[#00D4AA]" },
    dark: { wrapper: "bg-white/5 border border-white/10", label: "text-white/60", icon: "text-[#FFD700]", pill: "bg-white/10 border border-white/15 text-white", dot: "bg-[#00D4AA]" },
  };
  const s = styles[variant];
  return (
    <div className={`rounded-2xl p-4 md:p-5 ${s.wrapper}`}>
      <div className="flex items-center gap-2 mb-3">
        <CalendarDays size={16} className={s.icon} />
        <span className={`text-[10px] font-bold uppercase tracking-widest ${s.label}`}>Upcoming Dates</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {dates.map((d, i) => (
          <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${s.pill}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
            {d.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const CTABanner = ({ variant = "blue" }) => {
  const variants = {
    blue: { bg: "bg-[#0087E6]", text: "text-white", subtext: "text-white/80", button: "bg-white text-[#0087E6] hover:bg-[#FFD700] hover:text-[#002B5C]" },
    navy: { bg: "bg-[#002B5C]", text: "text-white", subtext: "text-white/70", button: "bg-[#FFD700] text-[#002B5C] hover:bg-white" },
    gold: { bg: "bg-[#FFD700]", text: "text-[#002B5C]", subtext: "text-[#002B5C]/70", button: "bg-[#002B5C] text-white hover:bg-[#0087E6]" },
  };
  const v = variants[variant];
  return (
    <section className={`${v.bg} py-12 md:py-16`}>
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
          <div className="text-center md:text-left">
            <h3 className={`font-anton text-2xl md:text-3xl lg:text-4xl uppercase ${v.text}`}>Ready to Start Your Pizza Journey?</h3>
            <p className={`mt-2 ${v.subtext} text-sm md:text-base`}>Limited spots available. Secure yours today.</p>
          </div>
          <a href="#apply" className={`${v.button} px-8 py-4 font-anton text-lg uppercase tracking-wider rounded-full transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-3 group whitespace-nowrap`}>
            <ChefHat size={20} /> Register Now <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   HERO
────────────────────────────────────────────────────────────── */
const Hero = () => (
  <section className="relative min-h-screen w-full overflow-hidden bg-[#002B5C]">
    <div className="absolute inset-0">
      <img src="/img/academy/d10pizza-academy-hero.jpeg" alt="Pizza background" className="w-full h-full object-cover" loading="eager" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#002B5C]/80 via-[#002B5C]/60 to-[#002B5C]" />
      <div className="absolute inset-0 bg-[#0087E6]/20 mix-blend-multiply" />
    </div>
    <div className="absolute top-20 left-10 w-32 h-32 md:w-64 md:h-64 border border-white/10 rounded-full" />
    <div className="absolute bottom-40 right-10 w-24 h-24 md:w-48 md:h-48 border border-[#0087E6]/30 rounded-full" />
    <div className="relative z-10 min-h-screen flex flex-col justify-center px-4 pt-20">
      <div className="container mx-auto">
        <div className="max-w-4xl">
          <div className="mb-6"><Badge variant="light">Training Ground • Small groups • Big energy</Badge></div>
          <h1 className="font-anton text-[14vw] sm:text-[12vw] md:text-[10vw] lg:text-[8vw] leading-[0.85] uppercase text-white">
            Pizza<br /><span className="text-[#FFD700]">Academy</span>
          </h1>
          <p className="mt-6 md:mt-8 font-serif italic text-xl md:text-2xl lg:text-3xl text-white/80 max-w-xl leading-relaxed">
            "From your first dough stretch to your first proper service."
          </p>
          <div className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4">
            <a href="#apply" className="group inline-flex items-center justify-center gap-3 px-8 py-4 md:px-10 md:py-5 bg-[#FFD700] text-[#002B5C] font-anton text-base md:text-lg uppercase tracking-wider rounded-full hover:bg-white transition-all duration-200 shadow-xl">
              <ChefHat size={20} /> Register Now <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </a>
            <a href="#courses" className="inline-flex items-center justify-center gap-3 px-8 py-4 md:px-10 md:py-5 bg-[#0087E6] text-white font-anton text-base md:text-lg uppercase tracking-wider rounded-full hover:bg-white hover:text-[#002B5C] transition-all duration-200 shadow-xl shadow-[#0087E6]/30">
              View Courses
            </a>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
        <span className="text-[10px] uppercase tracking-widest">Scroll</span>
        <ArrowDown size={18} />
      </div>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   STATS BAR
────────────────────────────────────────────────────────────── */
const StatsBar = () => (
  <section className="relative z-20 -mt-1">
    <div className="bg-[#0087E6]">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-3">
          {STATS.map((stat, i) => (
            <Reveal key={i} delay={i * 0.03}>
              <div className={`py-8 md:py-10 text-center ${i < 2 ? "border-b sm:border-b-0 sm:border-r border-white/10" : ""}`}>
                <stat.icon className="w-6 h-6 mx-auto mb-3 text-white/70" />
                <div className="font-anton text-3xl md:text-4xl lg:text-5xl text-white">{stat.value}</div>
                <div className="text-[10px] md:text-xs uppercase tracking-widest text-white/70 mt-1">{stat.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   METHODOLOGY
────────────────────────────────────────────────────────────── */
const Methodology = () => {
  const pillars = [
    { icon: Wheat, title: "The Basics Done Right", desc: "We teach the few things that actually matter, then we repeat until it sticks." },
    { icon: Flame, title: "Hands-On Technique", desc: "Stretching, topping, timing, pacing. You learn by doing, not by watching." },
    { icon: TrendingUp, title: "Real-World Thinking", desc: "For pros and owners: routines, service flow, pricing basics, and daily focus." },
  ];
  return (
    <section id="method" className="py-20 md:py-32 bg-white overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <SectionHeader eyebrow="The Vibe" title={<>Learn Properly.<br /><span className="text-[#0087E6]">Have Fun.</span></>}
              subtitle={<>Friendly coaching, clear steps, and a lot of pizza — plus <span className="font-bold text-[#002B5C]">accommodation</span>, local experiences and time to actually enjoy where you are. No ego, no stress.</>}
              center={false} />
            <div className="space-y-6 mt-8">
              {pillars.map((pillar, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="group flex gap-5 p-5 rounded-2xl border border-gray-100 hover:border-[#0087E6]/30 hover:bg-[#F0F9FF] transition-all duration-200">
                    <div className="w-14 h-14 flex-shrink-0 bg-[#0087E6]/10 rounded-xl flex items-center justify-center group-hover:bg-[#0087E6] transition-colors duration-200">
                      <pillar.icon className="w-6 h-6 text-[#0087E6] group-hover:text-white transition-colors duration-200" />
                    </div>
                    <div>
                      <h3 className="font-anton text-xl text-[#002B5C] uppercase mb-1">{pillar.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{pillar.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <a href="#apply" className="mt-8 inline-flex items-center gap-2 text-[#0087E6] font-bold hover:text-[#002B5C] transition-colors group">
                <span>Start learning today</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.08}>
            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden"><img src="/img/academy/d10pizza-academy-1.jpg" alt="Pizza making" className="w-full h-full object-cover" /></div>
                  <div className="aspect-square rounded-2xl overflow-hidden"><img src="/img/academy/d10pizza-academy-4.jpg" alt="Dough" className="w-full h-full object-cover" /></div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="aspect-square rounded-2xl overflow-hidden"><img src="/img/academy/d10pizza-academy-3.jpg" alt="Fresh pizza" className="w-full h-full object-cover" /></div>
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden"><img src="/img/academy/d10pizza-academy-14.jpg" alt="Kitchen vibe" className="w-full h-full object-cover" /></div>
                </div>
              </div>
              <div className="bg-[#002B5C] text-white p-6 md:p-8 rounded-2xl shadow-2xl">
                <Quote className="w-8 h-8 text-[#0087E6] mb-3" />
                <p className="font-anton text-xl md:text-2xl leading-[1.1] uppercase text-[#FFD700]">"We keep it simple. Then we make it consistent."</p>
                <div className="mt-4 text-sm text-white/60">D10 Academy motto</div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   COURSES
────────────────────────────────────────────────────────────── */
const CourseCard = ({ course, onSelect, isExpanded = false, isMobile = false }) => {
  const [showDetails, setShowDetails] = useState(isExpanded);
  const detailsVisible = isMobile ? showDetails : isExpanded;
  return (
    <div className={`bg-white rounded-2xl overflow-hidden shadow-lg border ${detailsVisible ? "border-[#0087E6]" : "border-gray-100"} transition-all duration-200`}>
      <div className={`p-6 md:p-8 ${isMobile ? "cursor-pointer" : "cursor-default"}`} onClick={() => isMobile && setShowDetails(!showDetails)}>
        <div className="flex items-center justify-between mb-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${detailsVisible ? "bg-[#0087E6]" : "bg-[#0087E6]/10"}`}>
            <course.icon size={24} className={detailsVisible ? "text-white" : "text-[#0087E6]"} />
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${detailsVisible ? "bg-[#0087E6] text-white" : "bg-gray-100 text-gray-500"}`}>{course.level}</span>
        </div>
        <h3 className="font-anton text-2xl md:text-3xl uppercase text-[#002B5C] mb-1">{course.title}</h3>
        <p className="text-gray-500 text-sm mb-4">{course.subtitle}</p>
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
          <Clock size={14} /><span>{course.duration}</span><span className="mx-2">•</span><span>{course.sessions}</span>
        </div>
        {!detailsVisible && course.dates && course.dates.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-[#0087E6]">
            <CalendarDays size={14} /><span className="font-semibold">Next: {course.dates[0].label}</span>
            {course.dates.length > 1 && <span className="text-gray-400">+{course.dates.length - 1} more</span>}
          </div>
        )}
        {isMobile && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm font-bold text-[#0087E6]">{showDetails ? "Hide details" : "Show details"}</span>
            <ArrowDown size={16} className={`text-[#0087E6] transition-transform ${showDetails ? "rotate-180" : ""}`} />
          </div>
        )}
      </div>
      <AnimatePresence initial={false}>
        {detailsVisible && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
            <div className="px-6 md:px-8 pb-6 md:pb-8 border-t border-gray-100">
              <div className="relative h-48 md:h-64 -mx-6 md:-mx-8 mb-6">
                <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-6 right-6">
                  <p className="font-anton text-lg text-[#FFD700] uppercase">"{course.tagline}"</p>
                  <p className="text-white/70 text-sm mt-1">{course.target}</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">{course.description}</p>
              {course.dates && course.dates.length > 0 && <div className="mb-6"><UpcomingDates dates={course.dates} variant="light" /></div>}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {course.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-[#00D4AA]/20 flex items-center justify-center flex-shrink-0 mt-0.5"><Check size={12} className="text-[#00D4AA]" /></div>
                    <span className="text-sm text-gray-600">{h}</span>
                  </div>
                ))}
              </div>
              <div className="mb-6">
                <h4 className="font-bold text-[#002B5C] uppercase tracking-wider text-xs mb-4 flex items-center gap-2"><BookOpen size={14} className="text-[#0087E6]" /> What You Learn</h4>
                <div className="grid gap-2">
                  {course.modules.map((m, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <span className="w-6 h-6 rounded-full bg-[#0087E6] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                      <div className="flex-grow"><span className="font-medium text-[#002B5C] text-sm">{m.title}</span><span className="text-gray-400 text-xs ml-2 hidden sm:inline"> - {m.desc}</span></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mb-6">
                <h4 className="font-bold text-[#002B5C] uppercase tracking-wider text-xs mb-3 flex items-center gap-2"><ShoppingBag size={14} className="text-[#0087E6]" /> Included</h4>
                <div className="flex flex-wrap gap-2">{course.kit.map((k, i) => <span key={i} className="px-3 py-1.5 bg-[#0087E6]/10 text-[#0087E6] text-xs font-bold rounded-full">{k}</span>)}</div>
              </div>
              <button onClick={() => onSelect(course.subtitle)} className="w-full px-8 py-4 bg-[#0087E6] text-white font-bold uppercase tracking-wider rounded-xl hover:bg-[#002B5C] transition-colors duration-200 flex items-center justify-center gap-2 group">
                Enroll Now <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CoursesSection = ({ onSelectCourse }) => {
  const [activeCourse, setActiveCourse] = useState("captain");
  return (
    <section id="courses" className="py-20 md:py-32 bg-[#F0F9FF]">
      <div className="container mx-auto px-4">
        <SectionHeader eyebrow="Training Programs" title="Pick Your Level" subtitle="Starter, pro track, or owner mode. Choose what matches your goal." />
        {/* MOBILE */}
        <div className="md:hidden space-y-4">
          {Object.values(COURSES).map((course) => <Reveal key={course.id}><CourseCard course={course} onSelect={onSelectCourse} isMobile /></Reveal>)}
        </div>
        {/* DESKTOP */}
        <div className="hidden md:block">
          <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-12">
            {Object.values(COURSES).map((c, index) => (
              <Reveal key={c.id} delay={index * 0.03}>
                <button onClick={() => setActiveCourse(c.id)} className={`w-full text-left p-6 md:p-8 rounded-2xl transition-all duration-200 ${activeCourse === c.id ? "bg-[#002B5C] text-white shadow-2xl scale-[1.01]" : "bg-white text-[#002B5C] hover:shadow-lg border border-gray-100"}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${activeCourse === c.id ? "bg-[#0087E6]" : "bg-[#0087E6]/10"}`}><c.icon size={24} className={activeCourse === c.id ? "text-white" : "text-[#0087E6]"} /></div>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${activeCourse === c.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>{c.level}</span>
                  </div>
                  <h3 className="font-anton text-2xl md:text-3xl uppercase mb-1">{c.title}</h3>
                  <p className={`text-sm mb-4 ${activeCourse === c.id ? "text-white/70" : "text-gray-500"}`}>{c.subtitle}</p>
                  <div className={`flex items-center gap-2 text-sm ${activeCourse === c.id ? "text-white/60" : "text-gray-400"}`}><Clock size={14} /><span>{c.duration}</span><span className="mx-2">•</span><span>{c.sessions}</span></div>
                  {c.dates && c.dates.length > 0 && <div className={`flex items-center gap-2 text-xs mt-3 ${activeCourse === c.id ? "text-[#FFD700]" : "text-[#0087E6]"}`}><CalendarDays size={13} /><span className="font-semibold">Next: {c.dates[0].label}</span></div>}
                  {activeCourse === c.id && <div className="mt-4 pt-4 border-t border-white/20"><span className="text-[#0087E6] text-sm font-bold flex items-center gap-2"><Check size={16} /> Selected</span></div>}
                </button>
              </Reveal>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={activeCourse} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100">
              {(() => {
                const course = COURSES[activeCourse];
                return (
                  <div className="grid lg:grid-cols-2">
                    <div className="relative h-64 md:h-80 lg:h-auto lg:min-h-[500px]">
                      <img src={course.image} alt={course.title} className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 lg:p-10">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: course.color }}><course.icon size={28} className="text-white" /></div>
                          <div><h3 className="font-anton text-3xl md:text-4xl text-white uppercase">{course.title}</h3><p className="text-white/70">{course.target}</p></div>
                        </div>
                        <p className="font-anton text-xl text-[#FFD700] uppercase">"{course.tagline}"</p>
                      </div>
                    </div>
                    <div className="p-6 md:p-8 lg:p-10">
                      <p className="text-gray-600 leading-relaxed mb-8">{course.description}</p>
                      {course.dates && course.dates.length > 0 && <div className="mb-8"><UpcomingDates dates={course.dates} variant="light" /></div>}
                      <div className="grid grid-cols-2 gap-3 mb-8">
                        {course.highlights.map((h, i) => (
                          <div key={i} className="flex items-start gap-2"><div className="w-5 h-5 rounded-full bg-[#00D4AA]/20 flex items-center justify-center flex-shrink-0 mt-0.5"><Check size={12} className="text-[#00D4AA]" /></div><span className="text-sm text-gray-600">{h}</span></div>
                        ))}
                      </div>
                      <div className="mb-8">
                        <h4 className="font-bold text-[#002B5C] uppercase tracking-wider text-xs mb-4 flex items-center gap-2"><BookOpen size={14} className="text-[#0087E6]" /> What You Learn</h4>
                        <div className="grid gap-2">
                          {course.modules.map((m, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                              <span className="w-6 h-6 rounded-full bg-[#0087E6] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                              <div className="flex-grow"><span className="font-medium text-[#002B5C] text-sm">{m.title}</span><span className="text-gray-400 text-xs ml-2 hidden sm:inline"> - {m.desc}</span></div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="mb-8">
                        <h4 className="font-bold text-[#002B5C] uppercase tracking-wider text-xs mb-3 flex items-center gap-2"><ShoppingBag size={14} className="text-[#0087E6]" /> Included</h4>
                        <div className="flex flex-wrap gap-2">{course.kit.map((k, i) => <span key={i} className="px-3 py-1.5 bg-[#0087E6]/10 text-[#0087E6] text-xs font-bold rounded-full">{k}</span>)}</div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-4 items-center pt-6 border-t border-gray-100">
                        <button onClick={() => onSelectCourse(course.subtitle)} className="w-full sm:w-auto sm:ml-auto px-8 py-4 bg-[#0087E6] text-white font-bold uppercase tracking-wider rounded-xl hover:bg-[#002B5C] transition-colors duration-200 flex items-center justify-center gap-2 group">
                          Enroll Now <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   PARTNER NETWORK CTA (compact league board → /partners)
────────────────────────────────────────────────────────────── */
const PartnerNetworkCTA = () => (
  <section className="py-16 md:py-24 bg-[#002B5C] relative overflow-hidden">
    <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#0087E6] rounded-full blur-[200px] opacity-15 translate-x-1/2 -translate-y-1/2" />
    <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#FFD700] rounded-full blur-[160px] opacity-10 -translate-x-1/2 translate-y-1/2" />
    <div className="container mx-auto px-4 relative z-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 md:mb-14">
        <div>
          <Reveal><span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-4 bg-[#FFD700] text-[#002B5C]">After The Academy</span></Reveal>
          <Reveal delay={0.05}><h2 className="font-anton text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9] text-white">The Transfer <span className="text-[#0087E6]">Market</span></h2></Reveal>
          <Reveal delay={0.1}><p className="mt-4 font-serif italic text-lg md:text-xl text-white/60 max-w-xl">Finish the course. Get placed in a top kitchen. We scout, you play.</p></Reveal>
        </div>
        <Reveal delay={0.12}>
          <div className="flex items-center gap-5 bg-white/5 backdrop-blur border border-white/10 px-6 py-4 rounded-2xl">
            <div className="text-center"><div className="font-anton text-2xl text-[#FFD700]">{PARTNER_JOBS.length}</div><div className="text-[10px] uppercase font-bold text-white/40 tracking-widest">Open Roles</div></div>
            <div className="w-px h-10 bg-white/10" />
            <div className="text-center"><div className="font-anton text-2xl text-white">100%</div><div className="text-[10px] uppercase font-bold text-white/40 tracking-widest">Verified</div></div>
          </div>
        </Reveal>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PARTNER_JOBS.map((job, i) => (
          <Reveal key={i} delay={i * 0.04}>
            <div className="group bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 hover:border-[#0087E6]/40 hover:bg-white/10 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-white/10"><img src={job.logoUrl} alt={job.pizzeria} className="w-full h-full object-contain" /></div>
                <div className="min-w-0">
                  <h4 className="font-anton text-base uppercase text-white leading-tight truncate">{job.pizzeria}</h4>
                  <div className="flex items-center gap-1 text-white/40 mt-0.5"><MapPin size={10} /><span className="text-[10px] font-bold uppercase tracking-wider truncate">{job.location}</span></div>
                </div>
              </div>
              <div className="mb-3"><span className="font-bold text-white text-sm">{job.role}</span></div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/30 bg-white/5 px-2.5 py-1 rounded-full">{job.type}</span>
                <div className="w-7 h-7 rounded-full bg-[#0087E6]/20 flex items-center justify-center group-hover:bg-[#0087E6] transition-colors"><Briefcase size={12} className="text-[#0087E6] group-hover:text-white transition-colors" /></div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/partners" className="inline-flex items-center gap-3 px-8 py-4 bg-[#FFD700] text-[#002B5C] font-anton text-lg uppercase tracking-wider rounded-full hover:bg-white transition-all duration-300 shadow-lg shadow-[#FFD700]/20 group">
            <Building2 size={20} /> View All Open Roles <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-white/40 text-sm">or <Link to="/partners" className="text-[#0087E6] hover:text-white transition-colors font-bold underline underline-offset-2">learn how the transfer market works</Link></p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   INSTRUCTORS
────────────────────────────────────────────────────────────── */
const InstructorsSection = () => (
  <section className="py-20 md:py-32 bg-[#F0F9FF]">
    <div className="container mx-auto px-4">
      <SectionHeader eyebrow="The Mentors" title="Instructors" subtitle="The ones who set the standard and taught us what 'good' really means." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {INSTRUCTORS.map((m, i) => (
          <Reveal key={i} delay={i * 0.03}>
            <div className="group bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:border-[#0087E6]/25 hover:shadow-xl transition-all duration-200">
              <div className="relative aspect-[3/4] bg-slate-100 overflow-hidden">
                <img src={m.img} alt={m.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#002B5C]/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-6 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full">
                    <GraduationCap size={14} className="text-[#FFD700]" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white">Legend Status • {m.origin}</span>
                  </div>
                </div>
              </div>
              <div className="p-6 text-center">
                <h3 className="font-anton text-2xl uppercase text-[#002B5C]">{m.name}</h3>
                <p className="mt-2 text-xs font-bold uppercase tracking-widest text-gray-500">{m.role}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.12}>
        <div className="mt-12 text-center">
          <a href="#apply" className="inline-flex items-center gap-3 px-8 py-4 bg-[#002B5C] text-white font-anton text-lg uppercase tracking-wider rounded-full hover:bg-[#0087E6] transition-all duration-300 shadow-lg group">
            <ChefHat size={20} /> Learn With The Team <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   SPECIAL GUESTS
────────────────────────────────────────────────────────────── */
const SpecialGuestsSection = () => (
  <section className="py-20 md:py-32 bg-white border-t border-slate-100">
    <div className="container mx-auto px-4">
      <SectionHeader eyebrow="Champions League" title={<>World Class <span className="text-[#0087E6]">Masters</span></>} subtitle="Sometimes you want more than tips. You want the real standard." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-5xl mx-auto">
        {SPECIAL_GUESTS.map((m, i) => (
          <Reveal key={i} delay={i * 0.04}>
            <div className="group rounded-3xl overflow-hidden shadow-2xl border border-gray-100 hover:border-[#FFD700]/60 transition-colors duration-300 bg-white">
              <div className="relative aspect-[4/3] bg-slate-50 overflow-hidden">
                <img src={m.img} alt={m.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute top-4 right-4 bg-[#FFD700] text-[#002B5C] p-2.5 rounded-full shadow-lg"><Award size={20} /></div>
                <div className="absolute bottom-0 left-0 right-0 p-7 md:p-8">
                  <div className="inline-flex items-center gap-2 mb-3 bg-[#0087E6] px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white">
                    <Medal size={12} className="text-[#FFD700]" /> Master Status • {m.origin}
                  </div>
                  <h3 className="font-anton text-3xl md:text-4xl uppercase text-white leading-[0.95]">{m.name}</h3>
                  <p className="mt-2 text-white/85 font-medium leading-relaxed">{m.role}</p>
                  {m.headline && (
                    <div className="mt-3 inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full">
                      <Star size={14} className="text-[#FFD700]" /><span className="text-[10px] font-bold uppercase tracking-widest text-white">{m.headline}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-6 md:p-7 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-400">Guest master</div>
                  <div className="flex items-center gap-1 text-[#FFD700]"><Star size={14} /><Star size={14} /><Star size={14} /></div>
                </div>
                <div className="mt-4 rounded-2xl border border-[#0087E6]/15 bg-[#F0F9FF] p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2"><BookOpen size={16} className="text-[#0087E6]" /><div className="text-[10px] font-bold uppercase tracking-widest text-[#002B5C]/70">Why this matters</div></div>
                  <p className="text-sm text-gray-600 leading-relaxed">{m.details}</p>
                </div>
                <a href="#apply" className="mt-5 inline-flex items-center gap-2 text-[#0087E6] font-bold hover:text-[#002B5C] transition-colors group">
                  <span>Join a session with masters</span><ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   GALLERY
────────────────────────────────────────────────────────────── */
const GallerySection = () => (
  <section className="py-20 md:py-32 bg-white">
    <div className="container mx-auto px-4">
      <SectionHeader eyebrow="Behind The Scenes" title="Life At The Academy" subtitle="A few moments from the kitchen and the table." />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
        {GALLERY.map((img, i) => (
          <Reveal key={i} delay={i * 0.02}>
            <div className="relative overflow-hidden rounded-xl md:rounded-2xl group">
              <div className="aspect-[4/3]"><img src={img.url} alt={img.alt} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" /></div>
              <div className="absolute inset-0 bg-[#002B5C]/0 group-hover:bg-[#002B5C]/20 transition-colors duration-300" />
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="mt-12 text-center">
          <a href="#apply" className="inline-flex items-center gap-3 px-8 py-4 bg-[#002B5C] text-white font-anton text-lg uppercase tracking-wider rounded-full hover:bg-[#0087E6] transition-all duration-300 shadow-lg group">
            <ChefHat size={20} /> Join The Next Class <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   EVENTS
────────────────────────────────────────────────────────────── */
const EventsSection = () => (
  <section id="events" className="py-20 md:py-32 bg-[#F0F9FF]">
    <div className="container mx-auto px-4">
      <SectionHeader eyebrow="Extra Time" title="Special Events" subtitle="Not ready for a full course? Try one of these easy sessions." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {EVENTS.map((evt, i) => (
          <Reveal key={i} delay={i * 0.03}>
            <div className="group bg-white rounded-2xl p-6 md:p-8 hover:shadow-xl border border-transparent hover:border-[#0087E6]/20 transition-all duration-200 h-full flex flex-col">
              <div className="w-14 h-14 bg-[#0087E6]/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#0087E6] transition-colors duration-200">
                <evt.icon className="w-6 h-6 text-[#0087E6] group-hover:text-white transition-colors duration-200" />
              </div>
              <h3 className="font-anton text-xl md:text-2xl text-[#002B5C] uppercase mb-2">{evt.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4 flex-grow">{evt.desc}</p>
              <div className="pt-4 border-t border-gray-100"><span className="text-[10px] uppercase tracking-wider text-gray-400">{evt.capacity}</span></div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <div className="mt-12 text-center">
          <p className="text-gray-500 mb-4">Interested in a special event?</p>
          <a href="#apply" className="inline-flex items-center gap-2 text-[#0087E6] font-bold hover:text-[#002B5C] transition-colors group">
            <span>Get in touch</span><ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ──────────────────────────────────────────────────────────────
   FAQ
────────────────────────────────────────────────────────────── */
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section id="faq" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4 max-w-4xl">
        <SectionHeader eyebrow="Questions?" title="FAQ" subtitle="Everything you need before you join." />
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <Reveal key={i} delay={i * 0.02}>
              <div className={`border rounded-xl overflow-hidden transition-all duration-200 ${openIndex === i ? "border-[#0087E6] bg-[#F0F9FF]" : "border-gray-200"}`}>
                <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left">
                  <span className="font-bold text-[#002B5C] pr-4">{item.q}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${openIndex === i ? "bg-[#0087E6] rotate-45" : "bg-gray-100"}`}>
                    <span className={`text-xl font-light ${openIndex === i ? "text-white" : "text-gray-400"}`}>+</span>
                  </div>
                </button>
                <AnimatePresence initial={false}>
                  {openIndex === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                      <div className="px-6 pb-5 text-gray-500 leading-relaxed">{item.a}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-12 text-center p-8 bg-[#F0F9FF] rounded-2xl border border-[#0087E6]/20">
            <p className="text-[#002B5C] font-bold mb-2">Still have questions?</p>
            <p className="text-gray-500 text-sm mb-4">We're happy to help. Reach out anytime.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={`mailto:${D10.email}`} className="flex items-center gap-2 text-[#0087E6] hover:text-[#002B5C] transition-colors"><Mail size={16} /><span>{D10.email}</span></a>
              <span className="hidden sm:block text-gray-300">|</span>
              <a href={`tel:+${D10.phoneE164}`} className="flex items-center gap-2 text-[#0087E6] hover:text-[#002B5C] transition-colors"><Phone size={16} /><span>{D10.phoneDisplay}</span></a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   REGISTRATION FORM
────────────────────────────────────────────────────────────── */
const RegistrationForm = ({ selectedCourse }) => {
  const MAKE_WEBHOOK_URL = "https://hook.eu2.make.com/cm61co498wsly2lo4tfssadgy2cylu4q";

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", course: selectedCourse || "", preferredDate: "", message: "",
    source: "D10 Pizza Academy", page: typeof window !== "undefined" ? window.location.href : "",
  });

  const [status, setStatus] = useState({ sending: false, success: false, error: "" });

  useEffect(() => {
    if (selectedCourse) setFormData((prev) => ({ ...prev, course: selectedCourse, preferredDate: "" }));
  }, [selectedCourse]);

  const resetStatus = () => setStatus({ sending: false, success: false, error: "" });

  const getAvailableDates = () => {
    if (!formData.course) return [];
    const matchedCourse = Object.values(COURSES).find((c) => c.subtitle === formData.course);
    if (matchedCourse && matchedCourse.dates) return matchedCourse.dates;
    return [];
  };

  const availableDates = getAvailableDates();
  const isEventSelected = EVENTS.some((e) => e.title === formData.course);
  
  // Custom checks for rendering correct date selection UI
  const showDatePicker = availableDates.length > 0;
  const isCourseWithoutDates = formData.course && !isEventSelected && availableDates.length === 0;
  const showDateSection = showDatePicker || isEventSelected || isCourseWithoutDates;

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetStatus();
    if (!MAKE_WEBHOOK_URL || MAKE_WEBHOOK_URL.includes("XXXXXXXX")) {
      setStatus({ sending: false, success: false, error: "Webhook URL is missing." });
      return;
    }
    setStatus({ sending: true, success: false, error: "" });
    try {
      const payload = { ...formData, submittedAt: new Date().toISOString(), userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "", language: typeof navigator !== "undefined" ? navigator.language : "" };
      const res = await fetch(MAKE_WEBHOOK_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) { const text = await res.text().catch(() => ""); throw new Error(`Webhook failed (${res.status}). ${text || "No response body."}`); }
      setStatus({ sending: false, success: true, error: "" });
      setFormData((prev) => ({ name: "", email: "", phone: "", course: selectedCourse || prev.course || "", preferredDate: "", message: "", source: "D10 Pizza Academy", page: typeof window !== "undefined" ? window.location.href : "" }));
    } catch (err) {
      setStatus({ sending: false, success: false, error: err?.message || "Something went wrong sending the form." });
    }
  };

  return (
    <section id="apply" className="relative overflow-hidden">
      {/* Top Hero Banner */}
      <div className="bg-[#002B5C] py-16 md:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#0087E6] rounded-full blur-[200px] opacity-20 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FFD700] rounded-full blur-[180px] opacity-15 translate-x-1/2 translate-y-1/2" />
        <div className="absolute top-10 right-10 w-32 h-32 md:w-48 md:h-48 border border-white/10 rounded-full" />
        <div className="absolute bottom-10 left-10 w-24 h-24 md:w-32 md:h-32 border border-[#0087E6]/30 rounded-full" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD700] text-[#002B5C] mb-6">
              <Sparkles size={16} /><span className="text-xs font-bold uppercase tracking-widest">Limited Spots Available</span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-anton text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase text-white leading-[0.85] mb-6">Join The<br /><span className="text-[#FFD700]">Academy</span></h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-serif italic text-xl md:text-2xl text-white/70 max-w-2xl mx-auto mb-8">"Your pizza journey starts with a single step. Take it today."</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
              <div className="flex items-center gap-2 text-white/80"><ShieldCheck size={20} className="text-[#00D4AA]" /><span className="text-sm">Secure Registration</span></div>
              <div className="flex items-center gap-2 text-white/80"><Calendar size={20} className="text-[#00D4AA]" /><span className="text-sm">Flexible Dates</span></div>
              <div className="flex items-center gap-2 text-white/80"><Zap size={20} className="text-[#00D4AA]" /><span className="text-sm">Fast Confirmation</span></div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Form Area */}
      <div className="bg-[#0087E6] py-16 md:py-24 relative">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white rounded-full blur-[200px] opacity-10" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl overflow-hidden shadow-2xl">
              <div className="p-8 md:p-12">
                <div className="text-center mb-10">
                  <div className="w-16 h-16 bg-[#0087E6]/10 rounded-2xl flex items-center justify-center mx-auto mb-4"><ChefHat size={32} className="text-[#0087E6]" /></div>
                  <h3 className="font-anton text-3xl md:text-4xl text-[#002B5C] uppercase mb-2">Save Your Spot</h3>
                  <p className="text-gray-500">Fill in your details and we'll confirm everything within 24 hours.</p>
                </div>

                {status.success && (
                  <div className="mb-6 rounded-2xl border border-[#00D4AA]/30 bg-[#00D4AA]/10 p-4 text-[#002B5C]">
                    <p className="font-bold">✅ Sent!</p><p className="text-sm text-[#002B5C]/80">We got your registration. We&apos;ll get back to you soon.</p>
                  </div>
                )}
                {status.error && (
                  <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-900">
                    <p className="font-bold">Something went wrong</p><p className="text-sm opacity-90">{status.error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Full Name *</label>
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-4 rounded-xl font-medium text-[#002B5C] outline-none transition-colors" placeholder="Your name" disabled={status.sending} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Email Address *</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-4 rounded-xl font-medium text-[#002B5C] outline-none transition-colors" placeholder="you@email.com" disabled={status.sending} />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Phone Number</label>
                      <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-4 rounded-xl font-medium text-[#002B5C] outline-none transition-colors" placeholder="+385..." disabled={status.sending} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Select Course</label>
                      <select value={formData.course} onChange={(e) => setFormData({ ...formData, course: e.target.value, preferredDate: "" })} className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-4 rounded-xl font-medium text-[#002B5C] outline-none transition-colors cursor-pointer" disabled={status.sending}>
                        <option value="">Choose a course...</option>
                        {Object.values(COURSES).map((c) => <option key={c.id} value={c.subtitle}>{c.title} - {c.subtitle}</option>)}
                        <optgroup label="Special Events">
                          {EVENTS.map((ev, i) => <option key={i} value={ev.title}>{ev.title}</option>)}
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  {/* Preferred Date */}
                  <AnimatePresence initial={false}>
                    {showDateSection && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                        <div>
                          <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                            <span className="flex items-center gap-2"><CalendarDays size={12} /> Preferred Date</span>
                          </label>
                          {showDatePicker ? (
                            <>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-3">
                                {availableDates.map((d) => (
                                  <button key={d.value} type="button" disabled={status.sending} onClick={() => setFormData({ ...formData, preferredDate: d.value })}
                                    className={`px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-all duration-200 flex items-center justify-center gap-2 ${formData.preferredDate === d.value ? "border-[#0087E6] bg-[#0087E6] text-white shadow-lg shadow-[#0087E6]/20" : "border-gray-200 bg-gray-50 text-[#002B5C] hover:border-[#0087E6]/50 hover:bg-[#F0F9FF]"}`}>
                                    {formData.preferredDate === d.value && <Check size={14} />}{d.label}
                                  </button>
                                ))}
                                <button type="button" disabled={status.sending} onClick={() => setFormData({ ...formData, preferredDate: FLEXIBLE_DATE_VALUE })}
                                  className={`px-4 py-3 rounded-xl text-sm font-semibold border-2 transition-all duration-200 flex items-center justify-center gap-2 ${formData.preferredDate === FLEXIBLE_DATE_VALUE ? "border-[#FFD700] bg-[#FFD700] text-[#002B5C] shadow-lg shadow-[#FFD700]/20" : "border-dashed border-gray-300 bg-white text-gray-500 hover:border-[#FFD700] hover:text-[#002B5C]"}`}>
                                  {formData.preferredDate === FLEXIBLE_DATE_VALUE && <Check size={14} />}{FLEXIBLE_DATE_LABEL}
                                </button>
                              </div>
                              <p className="text-xs text-gray-400">Pick a date or let us know you're flexible — we'll work it out together.</p>
                            </>
                          ) : (
                            <div className="bg-[#F0F9FF] border border-[#0087E6]/15 rounded-xl px-4 py-3">
                              <p className="text-sm text-gray-600">
                                <span className="font-semibold text-[#002B5C]">
                                  {isCourseWithoutDates ? "Mentorships & private programs are arranged on request." : "Events run on request."}
                                </span> Drop your preferred timeframe in the message below and we'll find the best slot for you.
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Message (Optional)</label>
                    <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} rows={4} className="w-full bg-gray-50 border-2 border-transparent focus:border-[#0087E6] px-4 py-4 rounded-xl font-medium text-[#002B5C] outline-none transition-colors resize-none" placeholder="Any goals, dietary needs, or questions?" disabled={status.sending} />
                  </div>

                  <button type="submit" disabled={status.sending}
                    className={`w-full py-5 font-anton text-xl uppercase tracking-wider rounded-xl transition-colors duration-200 shadow-lg flex items-center justify-center gap-3 group ${status.sending ? "bg-[#FFD700]/70 text-[#002B5C]/70 cursor-not-allowed" : "bg-[#FFD700] text-[#002B5C] hover:bg-[#002B5C] hover:text-white"}`}>
                    <Send size={20} />{status.sending ? "Sending..." : "Confirm Registration"}<ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                  </button>
                  <p className="text-center text-gray-400 text-xs">By registering, you agree to our Terms and Privacy Policy.</p>
                </form>

                <div className="mt-10 pt-8 border-t border-gray-100">
                  <p className="text-center text-gray-500 text-sm mb-4">Prefer to reach out directly?</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                    <a href={`tel:+${D10.phoneE164}`} className="flex items-center gap-2 text-[#0087E6] hover:text-[#002B5C] transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#0087E6]/10 flex items-center justify-center"><Phone size={18} className="text-[#0087E6]" /></div>
                      <span className="font-medium">{D10.phoneDisplay}</span>
                    </a>
                    <a href={`mailto:${D10.email}`} className="flex items-center gap-2 text-[#0087E6] hover:text-[#002B5C] transition-colors">
                      <div className="w-10 h-10 rounded-full bg-[#0087E6]/10 flex items-center justify-center"><Mail size={18} className="text-[#0087E6]" /></div>
                      <span className="font-medium">{D10.email}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   MAIN PAGE
────────────────────────────────────────────────────────────── */
export default function PizzaAcademyRebuilt() {
  const [selectedCourse, setSelectedCourse] = useState("");

  const handleCourseSelect = (courseName) => {
    setSelectedCourse(courseName);
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-white min-h-screen selection:bg-[#0087E6] selection:text-white overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;600;700;800&family=Playfair+Display:ital,wght@0,400;0,600;1,400&display=swap');
        .academy-page { font-family: 'Manrope', sans-serif; }
        .academy-page .font-anton { font-family: 'Anton', sans-serif; letter-spacing: 0.02em; }
        .academy-page .font-serif { font-family: 'Playfair Display', serif; }
        .font-anton { font-family: 'Anton', sans-serif; letter-spacing: 0.02em; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 10px; }
        ::-webkit-scrollbar-track { background: #F0F9FF; }
        ::-webkit-scrollbar-thumb { background: #0087E6; border-radius: 5px; }
        ::-webkit-scrollbar-thumb:hover { background: #002B5C; }
      `}</style>

      <Navbar />

      <main className="academy-page">
        <Hero />
        <StatsBar />
        <Methodology />
        <CTABanner variant="gold" />
        <CoursesSection onSelectCourse={handleCourseSelect} />
        <PartnerNetworkCTA />
        <InstructorsSection />
        <SpecialGuestsSection />
        <GallerySection />
        <CTABanner variant="navy" />
        <EventsSection />
        <FAQSection />
        <RegistrationForm selectedCourse={selectedCourse} />
      </main>

      <Footer />
    </div>
  );
}